import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  dialogTitleClasses,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  CloudDownload as PullIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { TableColumn } from '../types/table.types';
import { JiraService } from '../services/jira.service';
import { TableDataService } from '../services/tableData.service';
import {
  JIRA_RESPONSE_FIELDS,
  JiraPreset,
  JiraPullConfig,
  JiraPullResult,
} from '../types/jira.types';

const ACCENT = '#00875a';
const SKIP = '__skip__';

const HEAD_CELL_SX = { fontWeight: 700, bgcolor: 'rgba(0,135,90,0.06)' };

interface JiraPullDialogProps {
  open: boolean;
  onClose: () => void;
  tableId: string;
  dataId: string | null;
  row: Record<string, any> | null;
  columns: TableColumn[];
  canEdit: boolean;
  onApplied: () => void;
}

export default function JiraPullDialog({
  open,
  onClose,
  tableId,
  dataId,
  row,
  columns,
  canEdit,
  onApplied,
}: JiraPullDialogProps) {
  const [presets, setPresets] = useState<JiraPreset[]>([]);
  const [presetId, setPresetId] = useState('');
  const [presetName, setPresetName] = useState('');
  const [keyColumn, setKeyColumn] = useState('');
  const [fields, setFields] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<JiraPullResult | null>(null);
  const [picked, setPicked] = useState<Record<string, boolean>>({});
  const [applied, setApplied] = useState(false);

  const applyConfig = (preset: JiraPreset | null) => {
    const config = (preset?.config as JiraPullConfig) || null;
    setPresetName(preset?.name || '');
    setKeyColumn(config?.keyColumn || '');
    setFields({ ...(config?.fields || {}) });
  };

  const loadPresets = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const all = await JiraService.getPresets(tableId);
      const pullPresets = all.filter(p => p.type === 'pull');
      setPresets(pullPresets);
      if (pullPresets.length > 0) {
        setPresetId(pullPresets[0].id);
        applyConfig(pullPresets[0]);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Не удалось загрузить пресеты');
    } finally {
      setLoading(false);
    }
  }, [tableId]);

  useEffect(() => {
    if (!open) return;
    setPresets([]);
    setPresetId('');
    setPresetName('');
    setKeyColumn('');
    setFields({});
    setResult(null);
    setPicked({});
    setApplied(false);
    setError(null);
    loadPresets();
  }, [open, loadPresets]);

  const handleSelectPreset = (id: string) => {
    setPresetId(id);
    setResult(null);
    setApplied(false);
    applyConfig(presets.find(p => p.id === id) || null);
  };

  const handleSave = async (asNew: boolean) => {
    if (!presetName.trim()) {
      setError('Введите название пресета');
      return;
    }
    if (!keyColumn) {
      setError('Выберите колонку с ключом задачи');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const config: JiraPullConfig = { keyColumn, fields };
      const saved = asNew || !presetId
        ? await JiraService.createPreset(tableId, { name: presetName.trim(), type: 'pull', config })
        : await JiraService.updatePreset(tableId, presetId, { name: presetName.trim(), config });

      setPresets(prev => {
        const rest = prev.filter(p => p.id !== saved.id);
        return [...rest, saved].sort((a, b) => a.name.localeCompare(b.name));
      });
      setPresetId(saved.id);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Не удалось сохранить пресет');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!presetId) return;
    setSaving(true);
    try {
      await JiraService.deletePreset(tableId, presetId);
      const rest = presets.filter(p => p.id !== presetId);
      setPresets(rest);
      if (rest.length > 0) {
        setPresetId(rest[0].id);
        applyConfig(rest[0]);
      } else {
        setPresetId('');
        applyConfig(null);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Не удалось удалить пресет');
    } finally {
      setSaving(false);
    }
  };

  const handleFetch = async () => {
    if (!dataId || !presetId) return;
    setFetching(true);
    setError(null);
    setResult(null);
    setApplied(false);
    try {
      const res = await JiraService.pull(tableId, dataId, presetId);
      setResult(res);
      const initial: Record<string, boolean> = {};
      Object.keys(res.mapped).forEach(column => { initial[column] = true; });
      setPicked(initial);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Jira не вернула данные');
    } finally {
      setFetching(false);
    }
  };

  const handleApply = async () => {
    if (!dataId || !result) return;
    const chosen = Object.entries(result.mapped).filter(([column]) => picked[column]);
    if (chosen.length === 0) {
      setError('Отметьте хотя бы одно поле для записи');
      return;
    }
    setApplying(true);
    setError(null);
    try {
      const current = await TableDataService.getTableDataById(tableId, dataId);
      const merged = { ...(current.data || {}) };
      chosen.forEach(([column, value]) => { merged[column] = value; });

      await TableDataService.updateTableData(tableId, dataId, merged);
      setApplied(true);
      onApplied();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Не удалось записать данные в строку');
    } finally {
      setApplying(false);
    }
  };

  const keyPreview = useMemo(() => {
    if (!keyColumn) return null;
    const value = row?.[keyColumn];
    return value === undefined || value === null || value === '' ? null : String(value);
  }, [keyColumn, row]);

  const mappedEntries = result ? Object.entries(result.mapped) : [];

  return (
    <Dialog
      open={open}
      onClose={fetching || applying ? undefined : onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle component="div" sx={{ background: `linear-gradient(135deg, ${ACCENT} 0%, #36b37e 100%)`, color: 'white', pb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <PullIcon />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>Получить данные из Jira</Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ minHeight: 300, [`.${dialogTitleClasses.root} + &`]: { pt: 3 } }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress sx={{ color: ACCENT }} />
          </Box>
        ) : (
          <>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5, mb: 2 }}>
              <FormControl size="small" sx={{ minWidth: 240, maxWidth: 340 }}>
                <Select
                  value={presetId || ''}
                  displayEmpty
                  onChange={(e) => (e.target.value ? handleSelectPreset(e.target.value) : (setPresetId(''), applyConfig(null)))}
                >
                  <MenuItem value=""><em>{canEdit ? 'Новый пресет' : 'Пресет не выбран'}</em></MenuItem>
                  {presets.map(p => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
                </Select>
              </FormControl>

              {canEdit && (
                <>
                  <TextField
                    size="small"
                    label="Название пресета"
                    value={presetName}
                    onChange={(e) => setPresetName(e.target.value)}
                    sx={{ minWidth: 200 }}
                  />
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<SaveIcon />}
                    disabled={saving}
                    onClick={() => handleSave(false)}
                    sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
                  >
                    {presetId ? 'Сохранить' : 'Создать'}
                  </Button>
                  {presetId && (
                    <Tooltip title="Удалить пресет">
                      <span>
                        <IconButton size="small" color="error" disabled={saving} onClick={handleDelete}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                </>
              )}
            </Box>

            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Шаг 1. Откуда взять ключ задачи</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5, mb: 3 }}>
              <FormControl size="small" sx={{ minWidth: 260, maxWidth: 360 }}>
                <Select
                  value={keyColumn || ''}
                  displayEmpty
                  disabled={!canEdit}
                  onChange={(e) => setKeyColumn(e.target.value)}
                >
                  <MenuItem value=""><em>Колонка не выбрана</em></MenuItem>
                  {columns.map(c => <MenuItem key={c.id} value={c.name}>{c.name}</MenuItem>)}
                </Select>
              </FormControl>
              <Chip
                label={keyPreview ? `Ключ: ${keyPreview}` : 'В этой строке колонка пуста'}
                color={keyPreview ? 'default' : 'warning'}
                sx={keyPreview ? { bgcolor: 'rgba(0,135,90,0.1)', color: ACCENT, fontWeight: 600 } : undefined}
              />
            </Box>

            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Шаг 2. Куда записать ответ</Typography>
            <TableContainer sx={{ mb: 2 }}>
              <Table size="small" sx={{ tableLayout: 'fixed', minWidth: 560 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ ...HEAD_CELL_SX, width: '30%' }}>Поле ответа</TableCell>
                    <TableCell sx={{ ...HEAD_CELL_SX, width: '35%' }}>Колонка таблицы</TableCell>
                    <TableCell sx={HEAD_CELL_SX}>Пришло</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {JIRA_RESPONSE_FIELDS.map(meta => {
                    const target = fields[meta.name] || SKIP;
                    const received = result?.raw?.[meta.name];
                    return (
                      <TableRow key={meta.name} hover>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{meta.label}</Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', overflowWrap: 'anywhere' }}>
                            {meta.name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <FormControl fullWidth size="small">
                            <Select
                              value={target}
                              disabled={!canEdit}
                              onChange={(e) => setFields(prev => {
                                const next = { ...prev };
                                if (e.target.value === SKIP) delete next[meta.name];
                                else next[meta.name] = e.target.value;
                                return next;
                              })}
                            >
                              <MenuItem value={SKIP}><em>Не записывать</em></MenuItem>
                              {columns.map(c => <MenuItem key={c.id} value={c.name}>{c.name}</MenuItem>)}
                            </Select>
                          </FormControl>
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="caption"
                            color={received == null ? 'text.disabled' : 'text.secondary'}
                            sx={{ display: 'block', overflowWrap: 'anywhere' }}
                          >
                            {result ? (received == null ? 'пусто' : String(received)) : '—'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            {result && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                  Шаг 3. Что записать в строку {result.key}
                </Typography>
                {mappedEntries.length === 0 ? (
                  <Alert severity="warning" sx={{ borderRadius: 2 }}>
                    Jira вернула ответ, но все сопоставленные поля пустые — записывать нечего.
                  </Alert>
                ) : (
                  <Stack spacing={0.5}>
                    {mappedEntries.map(([column, value]) => (
                      <Stack key={column} direction="row" spacing={1} alignItems="center">
                        <Checkbox
                          size="small"
                          checked={Boolean(picked[column])}
                          onChange={(e) => setPicked(prev => ({ ...prev, [column]: e.target.checked }))}
                          sx={{ color: ACCENT, '&.Mui-checked': { color: ACCENT }, flexShrink: 0 }}
                        />
                        <Typography variant="body2" sx={{ fontWeight: 600, width: 180, flexShrink: 0, overflowWrap: 'anywhere' }}>
                          {column}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" noWrap title={String(value)} sx={{ minWidth: 0 }}>
                          {String(value)}
                        </Typography>
                      </Stack>
                    ))}
                  </Stack>
                )}
              </>
            )}

            {error && <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>{error}</Alert>}
            {applied && (
              <Alert severity="success" icon={<CheckCircleIcon fontSize="inherit" />} sx={{ mt: 2, borderRadius: 2 }}>
                Данные записаны в строку. Изменение попало в историю.
              </Alert>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions disableSpacing sx={{ p: 3, pt: 0, gap: 1, flexWrap: 'wrap' }}>
        <Button onClick={onClose} disabled={fetching || applying}>Закрыть</Button>
        <Button
          variant="outlined"
          onClick={handleFetch}
          disabled={fetching || !presetId || !dataId}
          startIcon={fetching ? <CircularProgress size={18} /> : <PullIcon />}
          sx={{ borderColor: ACCENT, color: ACCENT, '&:hover': { borderColor: ACCENT, bgcolor: 'rgba(0,135,90,0.08)' } }}
        >
          {fetching ? 'Запрос…' : 'Запросить из Jira'}
        </Button>
        <Button
          variant="contained"
          onClick={handleApply}
          disabled={applying || !result || mappedEntries.length === 0}
          startIcon={applying ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
          sx={{
            background: `linear-gradient(135deg, ${ACCENT} 0%, #36b37e 100%)`,
            '&:hover': { background: 'linear-gradient(135deg, #006644 0%, #2a9d6f 100%)' },
          }}
        >
          {applying ? 'Запись…' : 'Записать в строку'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}